import type { AuthStatus } from "../services/authService";

let setAuthStatusRef: ((status: AuthStatus | ((prev: AuthStatus) => AuthStatus)) => void) | null = null;
let setupComplete = false;

export function registerAuthStatusSetter(
  setter: (status: AuthStatus | ((prev: AuthStatus) => AuthStatus)) => void
): void {
  setAuthStatusRef = setter;
}

export function setupDevSubscriptionHelper(): void {
  if (import.meta.env.VITE_APP_ENV !== "development") {
    return;
  }

  if (setupComplete) {
    return;
  }

  setupComplete = true;

  window.setSubscription = (
    paidUntil: string | null | undefined,
    mkPaidUntil: string | null | undefined,
    scoutPaidUntil: string | null | undefined,
    estonianChartsEnabled: boolean | null | undefined,
  ) => {
    if (!setAuthStatusRef) {
      console.error(
        "setSubscription: Auth status setter not registered. Make sure you're logged in."
      );
      return;
    }

    // Update auth status using React's functional update pattern
    setAuthStatusRef((currentStatus: AuthStatus) => {
      if (!currentStatus.isAuthenticated || !currentStatus.profile) {
        console.error("setSubscription: User must be authenticated");
        return currentStatus;
      }

      const updatedProfile = {
        ...currentStatus.profile,
        ...(paidUntil !== undefined && { paid_until: paidUntil }),
        ...(mkPaidUntil !== undefined && { mk_paid_until: mkPaidUntil }),
        ...(scoutPaidUntil !== undefined && {
          scout_paid_until: scoutPaidUntil,
        }),
        ...(estonianChartsEnabled !== undefined && {
          estonian_charts: estonianChartsEnabled === true,
          entitlements: {
            ...((currentStatus.profile as Record<string, unknown>).entitlements &&
            typeof (currentStatus.profile as Record<string, unknown>)
              .entitlements === "object"
              ? ((currentStatus.profile as Record<string, unknown>)
                  .entitlements as Record<string, unknown>)
              : {}),
            estonian_charts: estonianChartsEnabled === true,
          },
        }),
      };

      const result: AuthStatus = {
        ...currentStatus,
        profile: updatedProfile,
      };

      console.log("Subscription updated:", {
        paid_until: paidUntil ?? "unchanged",
        mk_paid_until: mkPaidUntil ?? "unchanged",
        scout_paid_until: scoutPaidUntil ?? "unchanged",
        estonian_charts:
          estonianChartsEnabled === undefined
            ? "unchanged"
            : estonianChartsEnabled === true,
      });

      return result;
    });
  };

  console.log(
    "Development mode: Use window.setSubscription(paidUntil, mkPaidUntil, scoutPaidUntil, estonianChartsEnabled) to set subscription dates and Estonian charts entitlement"
  );
  console.log(
    "Example: window.setSubscription('2025-12-31T23:59:59Z', null, null, true)"
  );
  console.log(
    "To clear subscriptions, pass null: window.setSubscription(null, null, null, false)"
  );
}
